import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MapPin } from "lucide-react";
import { MaskLine, Reveal } from "@/lib/motion";
import { Marquee } from "@/components/Marquee";
import { Emblem } from "@/components/Brand";
import { Photo } from "@/components/Photo";
import { SITE, SERVICES, BENEFITS, PHOTOS } from "@/data/site";
import { BookingLink } from "@/components/BookingLink";
import { Testimonials } from "@/components/Testimonials";

const BTN_PRIMARY = "group inline-flex items-center gap-3 min-h-[48px] px-7 py-3.5 font-mono text-[13px] uppercase tracking-[0.14em] transition-all duration-300";

const Hero = () => (
  <section data-testid="hero-section" className="relative min-h-screen bg-cream overflow-hidden pt-20">
    <div className="absolute inset-0 opacity-[0.5] pointer-events-none"
      style={{ backgroundImage: "linear-gradient(#E2DACB 1px,transparent 1px)", backgroundSize: "100% 46px" }} />

    <div className="relative max-w-[1300px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-24 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        {/* Emblem stacks above the headline on mobile/tablet; desktop shows it beside the headline */}
        <div className="lg:hidden anim-up mb-10 inline-flex items-center justify-center rounded-full p-4"
          style={{ background: "radial-gradient(circle, #FDFBEE 55%, rgba(253,251,238,0) 72%)" }}>
          <Emblem eager sizes="(max-width: 640px) 128px, 160px" testId="hero-logo-mobile-img" className="w-32 sm:w-40 h-auto object-contain" />
        </div>
        <p className="anim-line font-mono text-[12px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-8" style={{ animationDelay: "0.1s" }}>
          Bookkeeping for Small Business
        </p>

        <h1 className="font-serif font-medium tracking-tight leading-[1.02] text-navy text-5xl sm:text-6xl lg:text-7xl">
          <MaskLine delay={0.15}>Order in the</MaskLine>
          <MaskLine delay={0.28}><span className="italic text-[#9a7a33]">numbers.</span></MaskLine>
          <MaskLine delay={0.41}>Clarity in the</MaskLine>
          <MaskLine delay={0.54}>vision.</MaskLine>
        </h1>

        <div className="anim-up mt-10 max-w-xl" style={{ animationDelay: "0.8s" }}>
          <p className="text-ink-muted text-lg leading-relaxed">
            Monthly QuickBooks bookkeeping for small business owners: transactions categorized,
            accounts reconciled, and a profit-and-loss statement and balance sheet delivered each month.
          </p>
          <p data-testid="hero-service-area" className="mt-5 flex items-start gap-2.5 text-[15px] text-navy leading-relaxed">
            <MapPin size={16} className="text-[#8a6b2f] shrink-0 mt-1" aria-hidden="true" />
            <span>{SITE.serviceArea}</span>
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <BookingLink placement="hero" testId="hero-consultation-btn" className={`${BTN_PRIMARY} bg-navy text-parchment border border-[#C5A059]/40 hover:-translate-y-0.5`}>
              Book a Consultation
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </BookingLink>
            <Link to="/contact" data-testid="hero-inquiry-btn"
              className={`${BTN_PRIMARY} text-navy border border-navy/30 hover:border-navy hover:bg-navy/5`}>
              Send an Inquiry
            </Link>
          </div>
          <p className="mt-8 text-[15px] text-ink-muted">
            <Link to="/services" data-testid="hero-packages-link" className="link-underline inline-flex items-center gap-2 text-navy hover:text-[#8a6b2f] transition-colors">
              Monthly bookkeeping packages from $250 <ArrowRight size={13} aria-hidden="true" />
            </Link>
            <span className="ml-2">— see what's included.</span>
          </p>
        </div>
      </div>

      {/* Approved emblem as the hero focal point; ivory radial backdrop blends it into the ledger-lined section */}
      <div className="lg:col-span-5 max-lg:hidden flex justify-center anim-up" style={{ animationDelay: "0.5s" }}>
        <div className="relative flex items-center justify-center w-[480px] h-[480px] rounded-full"
          style={{ background: "radial-gradient(circle, #FDFBEE 58%, rgba(253,251,238,0) 74%)" }}>
          <Emblem eager sizes="400px" testId="hero-logo-img" className="w-[400px] h-auto object-contain" />
        </div>
      </div>
    </div>
  </section>
);

const Benefits = () => (
  <section data-testid="benefits-section" className="bg-navy text-parchment">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-8">
      {BENEFITS.map((b, i) => (
        <Reveal key={b.t} delay={i * 0.08}>
          <div className="border-t border-[#24355A] pt-6">
            <p className="font-serif text-2xl lg:text-3xl text-gold leading-tight">{b.t}</p>
            <p className="text-parchment-muted text-[15px] mt-3 max-w-[16rem] leading-relaxed">{b.d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const ServicesOverview = () => (
  <section data-testid="services-overview-section" className="bg-cream py-28">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
        <Reveal>
          <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-5">The service</p>
          <h2 className="font-serif text-4xl lg:text-5xl text-navy tracking-tight max-w-xl leading-tight">
            Straightforward bookkeeping, done in QuickBooks
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link to="/services" data-testid="home-all-services-link" className="link-underline inline-flex items-center gap-2 min-h-[44px] font-mono text-[13px] uppercase tracking-[0.14em] text-navy">
            All services & packages <ArrowUpRight size={15} />
          </Link>
        </Reveal>
      </div>

      <div className="grid md:grid-cols-3 gap-px bg-[#E2DACB]">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.06}>
            <div data-testid={`service-card-${s.id}`}
              className="group bg-cream-ivory p-10 h-full hover:bg-cream-card transition-colors duration-500 relative">
              <div className="flex items-start justify-between">
                <span className="font-mono text-xs text-[#8a6b2f] tracking-[0.2em]">{s.num}</span>
                <ArrowUpRight size={20} className="text-navy/30 group-hover:text-[#8a6b2f] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-300" />
              </div>
              <h3 className="font-serif text-2xl lg:text-3xl text-navy mt-8 leading-snug">{s.title}</h3>
              <p className="text-ink-muted mt-4 leading-relaxed text-[15px]">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// Photo stacks above the text on mobile; sits beside it (with 56px gutter) on desktop.
const WorkingTogether = () => (
  <section data-testid="working-together-section" className="bg-cream py-28">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
      <Reveal className="lg:col-span-6">
        <Photo photo={PHOTOS.homePlanner} testId="home-photo" />
      </Reveal>
      <div className="lg:col-span-6">
        <Reveal>
          <p className="font-mono text-[12px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-5">How a month works</p>
          <h2 className="font-serif text-4xl lg:text-5xl text-navy tracking-tight leading-tight">
            What working with Oakford Ledger looks like
          </h2>
          <p className="text-ink-muted mt-6 leading-relaxed text-lg">
            Transactions are recorded as the month unfolds. Once statements arrive, each account is
            reconciled, your profit-and-loss statement and balance sheet are prepared, and a brief written
            summary points out anything worth your attention. Everything is handled remotely, on a
            dependable schedule, by the founder.
          </p>
          <Link to="/services" data-testid="home-how-it-works-link" className="link-underline inline-flex items-center gap-2 mt-8 min-h-[44px] font-mono text-[13px] uppercase tracking-[0.14em] text-navy">
            See the full process <ArrowUpRight size={15} />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const ConsultBanner = () => (
  <section data-testid="home-consultation-banner" className="bg-cream pb-28">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
      <div className="relative bg-navy text-parchment p-12 lg:p-20 border border-gold/30 overflow-hidden">
        <div className="relative max-w-2xl">
          <h2 className="font-serif text-4xl lg:text-6xl tracking-tight leading-[1.05]">
            Let's bring order to your books.
          </h2>
          <p className="text-parchment-muted mt-6 text-lg leading-relaxed">
            Book a consultation and you'll choose an available appointment through Google Calendar.
            Prefer to write first? Send an inquiry and we'll follow up by email.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <BookingLink placement="home-cta" testId="banner-consultation-btn" className={`${BTN_PRIMARY} bg-gold text-navy hover:bg-gold-hover`}>
              Book a Consultation
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </BookingLink>
            <Link to="/contact" data-testid="banner-inquiry-btn"
              className={`${BTN_PRIMARY} text-parchment border border-parchment/30 hover:border-gold hover:text-gold`}>
              Send an Inquiry
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Benefits />
      <ServicesOverview />
      <Marquee items={["Monthly Close", "Reconciliation", "P&L & Balance Sheet", "QuickBooks", "Serving Businesses Nationwide"]} tone="light" />
      <WorkingTogether />
      <Testimonials />
      <ConsultBanner />
    </>
  );
}
